import { useCallback, useRef, useState } from 'react';
import { warmUpRecaptcha } from '../lib/recaptcha';

/**
 * Client-side half of the lead-form bot defence.
 *
 * Owns the two signals that have to originate in the DOM, and that
 * reCAPTCHA alone doesn't give us:
 *
 *  - honeypot — a field hidden from humans. Anything that fills it in
 *    found it by parsing the markup, which people don't do.
 *  - elapsed  — milliseconds from form mount to submit. Humans type;
 *    scripted fills post more or less instantly.
 *
 * Both are forgeable by anyone who bothers to look, and that's fine.
 * They cost nothing, they run before any network call, and they catch
 * the high-volume unsophisticated traffic that makes up most form spam.
 * reCAPTCHA is the layer that handles bots which actually try.
 */
export interface LeadFormGuard {
  /** Spread onto <HoneypotField {...honeypot} />. */
  honeypot: {
    value: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  };
  /** Spread onto the <form> element. Warms reCAPTCHA on first focus. */
  formProps: {
    onFocus: () => void;
  };
  /** Extra payload fields for submitLead(). */
  getGuardFields: () => { website: string; elapsedMs: number };
}

export function useLeadFormGuard(): LeadFormGuard {
  const [website, setWebsite] = useState('');
  const warmed = useRef(false);

  // Ref, not state: this is read once at submit and must never trigger
  // a re-render. Initialised at first render, which is the closest we
  // get to "when the visitor saw the form".
  const mountedAt = useRef<number>(Date.now());

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWebsite(event.target.value);
  }, []);

  const getGuardFields = useCallback(
    () => ({
      website,
      elapsedMs: Date.now() - mountedAt.current,
    }),
    [website]
  );

  // onFocus (not onMount) so the script still stays off the wire for
  // anyone who scrolls past the form without engaging with it. Fires
  // once — <form onFocus> catches focus bubbling from any field.
  const onFocus = useCallback(() => {
    if (warmed.current) return;
    warmed.current = true;
    warmUpRecaptcha();
  }, []);

  return {
    honeypot: { value: website, onChange },
    formProps: { onFocus },
    getGuardFields,
  };
}
